import * as React from 'react'
import { CloseIcon } from '@/src/icons'

import { cn } from '@/registry/reapit/lib/utils'

// DS Chip: interactive pill, filter and selection variants.
// DS states: default, hovered (accent/action-lightest), focused, disabled, busy.
// Chip is interactive — unlike Tag which is read-only metadata.

type ChipVariant = 'filter' | 'selection'

type ChipProps = React.ComponentProps<'button'> & {
  variant?: ChipVariant
  selected?: boolean
  clearable?: boolean
  onClear?: (e: React.MouseEvent) => void
}

function Chip({
  className,
  variant = 'filter',
  selected = false,
  clearable = false,
  onClear,
  children,
  disabled,
  ...props
}: ChipProps) {
  return (
    <button
      type="button"
      data-slot="chip"
      data-variant={variant}
      data-selected={selected || undefined}
      disabled={disabled}
      aria-pressed={variant === 'filter' ? selected : undefined}
      className={cn(
        // DS: pill shape, 14 px text (text-sm), medium font, 12 px vertical (py-1.5) 12 px horizontal (px-3)
        'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors outline-none',
        'focus-visible:ring-[3px] focus-visible:ring-ring/50',
        'disabled:cursor-not-allowed disabled:opacity-50',
        // Filter variant: border-input, transparent bg; selected: action-lightest bg + action border + action text
        variant === 'filter' && [
          'border-input bg-background text-foreground',
          'hover:bg-accent hover:border-ring/50',
          selected && 'border-primary bg-accent text-fill-action-dark hover:bg-accent',
        ],
        // Selection variant (removable chip, e.g. multi-select pills):
        // neutral surface, slightly bordered
        variant === 'selection' && ['border-border bg-secondary text-secondary-foreground', 'hover:bg-muted'],
        className,
      )}
      {...props}
    >
      {children}
      {clearable && (
        <span
          data-slot="chip-clear"
          role="button"
          aria-label="Remove"
          tabIndex={-1}
          onClick={(e) => {
            e.stopPropagation()
            onClear?.(e)
          }}
          className="-mr-0.5 rounded-full p-0.5 opacity-60 hover:opacity-100"
        >
          <CloseIcon size="xs" />
        </span>
      )}
    </button>
  )
}

type InputChipVariant = 'known' | 'unknown' | 'invalid'

type InputChipProps = Omit<ChipProps, 'variant'> & {
  variant?: InputChipVariant
}

function InputChip({
  className,
  variant = 'known',
  selected,
  clearable = true,
  onClear,
  children,
  ...props
}: InputChipProps) {
  return (
    <span
      data-slot="input-chip"
      data-variant={variant}
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium',
        variant === 'known' && 'border-input bg-secondary text-secondary-foreground',
        variant === 'unknown' && 'border-border bg-muted text-muted-foreground',
        variant === 'invalid' && 'border-destructive/50 bg-destructive/10 text-destructive',
        className,
      )}
    >
      {children}
      {clearable && (
        <button
          type="button"
          data-slot="input-chip-clear"
          aria-label="Remove"
          onClick={onClear}
          className="-mr-0.5 rounded-full p-0.5 opacity-60 hover:opacity-100 focus:outline-none"
        >
          <CloseIcon size="xs" />
        </button>
      )}
    </span>
  )
}

type ChipGroupProps = React.ComponentProps<'div'> & {
  overflow?: 'wrap' | 'scroll'
}

function ChipGroup({ className, overflow = 'wrap', ...props }: ChipGroupProps) {
  return (
    <div
      data-slot="chip-group"
      data-overflow={overflow}
      className={cn(
        'flex gap-2',
        overflow === 'wrap' && 'flex-wrap',
        overflow === 'scroll' && 'overflow-x-auto scrollbar-none pb-0.5',
        className,
      )}
      {...props}
    />
  )
}

export { Chip, InputChip, ChipGroup }
export type { ChipProps, InputChipProps, ChipGroupProps, ChipVariant, InputChipVariant }
